/**
 * @fileoverview Billing status as a query, shared by the header, the wall and the
 * subscribe page. Five minutes stale: it changes when the gardener pays,
 * and the pages where that happens refetch it themselves.
 *
 * Where: everywhere that asks about the plan: the header's TrialLine, the
 * wall, the subscribe page, Free's room, AI access, the trial-used-up price.
 * API: GET /billing/status (getBillingStatus), asked in the app's language (the founder price comes back said in it, C101).
 * Settings: BILLING_STALE_MS (config/tunables/garden).
 *
 * useTendingFor is the app's one answer to "is this person a member tending
 * someone else's garden" (B23; B37 reuses it to lock the garden-level
 * settings a member must not change): the server's tendingFor from the same
 * status (the backend's one member signal, middleware/gardenContext
 * tendedOwnerOf, decides it), or null in their own garden. B37's locks read
 * it through shared/hooks/useOwnerOnly, so the plan and the locks agree. Choosing another garden empties the query cache
 * (chooseGarden), so it is re-read for the garden now chosen.
 *
 * @module useBilling
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getBillingStatus, type BillingStatus, type Price, type TendingFor } from '@/shared/api/billing';
import { useAuth } from '@/shared/hooks/useAuth';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { BILLING_STALE_MS } from '@/config/tunables/garden';

/** The billing status's cache key, invalidated after a checkout finishes. */
export const BILLING_QUERY_KEY = ['billing'] as const;

/** The gardener's billing status (trial, active, paused or Free; prices; AI access; Free's limits), once signed in. */
export function useBilling(): UseQueryResult<BillingStatus, Error> {
  const { mysqlUser } = useAuth();
  const { i18n: { language } } = useTranslation();
  return useQuery({
    // The language is part of the key: the status carries words the server says in it (a founder's price, C101).
    queryKey: [...BILLING_QUERY_KEY, language],
    queryFn: getBillingStatus,
    enabled: Boolean(mysqlUser),
    staleTime: BILLING_STALE_MS,
  });
}

/**
 * Whether the signed-in person is a member tending someone else's garden, as the server says (GET /billing/status
 * tendingFor): whose garden it is and the line to show; null in their own garden, and while the status is not in yet.
 *
 * @returns The owner's name and the line, or null.
 */
export function useTendingFor(): TendingFor | null {
  const { data } = useBilling();
  return data?.tendingFor ?? null;
}

/**
 * "$4.99", in the viewer's locale; whole amounts without the cents ("$40").
 *
 * @param price - The amount in the currency's minor unit (cents), as Stripe sends it.
 * @param locale - Whose way of writing money; the browser's when not given.
 */
export function formatPrice(price: Price, locale?: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: price.currency.toUpperCase(),
    minimumFractionDigits: price.amount % 100 === 0 ? 0 : 2,
  }).format(price.amount / 100);
}
